<script setup>
import {computed, onMounted, ref} from 'vue';
import { useSidebarState } from '../../composables/useSidebarState.js';
import AiAgentAiOrchestratorAvatar from '../agents/ai-orchestrator/avatar.vue';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import LogoIcon from "../logo/icon.vue";

const { isEffectivelyExpanded } = useSidebarState();

const props = defineProps({
    notificationCount: {
        type: Number,
        default: 0,
    },
    userProfileImageUrl: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['open-notifications']);

const showAiUsage = ref(false);
const aiUsageStartInPurchase = ref(false);
const showFlowie = ref(false);
const unreadSupportMessages = ref(0)
const delegatedGrants = ref([]);
const isBackOffice = computed(() => window.location.pathname.startsWith('/bo'));
const delegatedSession = computed(() => window.myflow?.delegatedAccess ?? { switched: false });
const showDelegatedSwitcher = computed(
    () => isBackOffice.value && !delegatedSession.value.switched && delegatedGrants.value.length > 0
);

function openFlowie() {
    showFlowie.value = !showFlowie.value;
}

function openSupport() {
    if (window.Intercom) {
        window.Intercom('show');
    }
}

function openAiUsage() {
    aiUsageStartInPurchase.value = false;
    showAiUsage.value = true;
}

// AI usage notifications link to `?ai-usage=purchase` (see AiUsageAlerts.php).
// The parameter is dropped again so a reload does not reopen the window.
function openAiUsageFromUrl() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('ai-usage') !== 'purchase') {
        return;
    }

    url.searchParams.delete('ai-usage');
    window.history.replaceState(window.history.state, '', url.toString());

    aiUsageStartInPurchase.value = true;
    showAiUsage.value = true;
}

function openNotifications() {
    emit('open-notifications');
}

const loadDelegatedAccess = async () => {
    try {
        const response = await axios.get(route('company.user.delegated-access.received'));
        delegatedGrants.value = response.data.grants ?? [];
    } catch (error) {
        delegatedGrants.value = [];
    }
};

const openDelegatedSwitcher = () => {
    window.dispatchEvent(new CustomEvent('delegated-access:open-switcher'));
};

const logout = async () => {
    try {
        await axios.post(route('logout'));
    } catch (e) {
        // ignore — we're navigating to /login regardless
    }
    window.location.replace(route('login'));
};


onMounted(() => {
    openAiUsageFromUrl();

    window.Intercom('onUnreadCountChange', function(unreadCount) {
        unreadSupportMessages.value = unreadCount
    });

    if (isBackOffice.value && window.myflow?.user?.loggedIn) {
        loadDelegatedAccess();
    }
})

</script>

<template>
    <div class="px-2 pt-1 pb-1.5 border-t border-gray-100">
        <!-- 4-column grid when expanded, 1-column when collapsed -->
        <div
            class="grid gap-1 items-stretch"
            :class="isEffectivelyExpanded ? 'grid-cols-3' : 'grid-cols-1'"
        >
            <!-- Profile -->
            <Menu as="div" class="relative">
                <MenuButton
                    class="flex w-full flex-col items-center justify-center rounded-xl py-1.5 hover:bg-brandPrimary-50 transition-colors"
                    :aria-label="_mft('navigation:backOffice.widget.profile')"
                >
                    <img
                        v-if="userProfileImageUrl"
                        :src="userProfileImageUrl"
                        alt="Profile picture"
                        class="w-6 h-6 object-cover rounded-full flex-shrink-0 aspect-square"
                    />
                    <div
                        v-else
                        class="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0"
                    >
                        <i
                            class="fa-light fa-user text-gray-500 text-sm"
                        ></i>
                    </div>

                    <span v-if="isEffectivelyExpanded" class="text-xxs text-gray-400 mt-0.5">
                        {{ _mft('navigation:backOffice.widget.profile') }}
                    </span>
                    <span
                        v-if="notificationCount > 0"
                        class="absolute top-0 left-1/2 flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-bold text-white bg-red-500 rounded-full"
                    >{{ notificationCount }}</span>
                </MenuButton>

                <transition
                    enter-active-class="transition duration-100 ease-out"
                    enter-from-class="transform scale-95 opacity-0"
                    enter-to-class="transform scale-100 opacity-100"
                    leave-active-class="transition duration-75 ease-in"
                    leave-from-class="transform scale-100 opacity-100"
                    leave-to-class="transform scale-95 opacity-0"
                >
                    <MenuItems
                        class="absolute bottom-full -translate-y-2 left-0 z-50 overflow-hidden rounded-lg bg-gray-900 text-gray-100 py-1 focus:outline-none"
                        :class="isEffectivelyExpanded ? 'w-48' : 'w-10'"
                    >
                        <MenuItem v-slot="{ active }">
                            <a
                                href="https://help.myflow.io/"
                                target="_blank"
                                class="flex items-center px-3 py-2 text-sm transition-colors"
                                :class="
                                    active
                                        ? 'bg-gray-700 text-white'
                                        : 'text-gray-200'
                                "
                            >
                                <i
                                    class="fa-light fa-life-ring w-5 text-center text-gray-400"
                                    :class="isEffectivelyExpanded ? 'mr-2' : ''"
                                ></i>
                                <template v-if="isEffectivelyExpanded">
                                    Help center
                                </template>
                            </a>
                        </MenuItem>
                        <MenuItem v-slot="{ active }">
                            <a
                                :href="route('user.edit')"
                                class="flex items-center px-3 py-2 text-sm transition-colors"
                                :class="
                                    active
                                        ? 'bg-gray-700 text-white'
                                        : 'text-gray-200'
                                "
                            >
                                <i
                                    class="fa-light fa-user w-5 text-center text-gray-400"
                                    :class="isEffectivelyExpanded ? 'mr-2' : ''"
                                ></i>
                                <template v-if="isEffectivelyExpanded">
                                    {{
                                        _mft(
                                            'navigation:backOffice.widget.account'
                                        )
                                    }}
                                </template>
                            </a>
                        </MenuItem>
                        <MenuItem
                            v-if="showDelegatedSwitcher"
                            v-slot="{ active }"
                        >
                            <button
                                type="button"
                                class="flex w-full items-center px-3 py-2 text-left text-sm transition-colors"
                                :class="
                                    active
                                        ? 'bg-gray-700 text-white'
                                        : 'text-gray-200'
                                "
                                @click="openDelegatedSwitcher"
                            >
                                <i
                                    class="fa-light fa-person-to-door w-5 text-center text-gray-400"
                                    :class="isEffectivelyExpanded ? 'mr-2' : ''"
                                ></i>
                                <template v-if="isEffectivelyExpanded">
                                    {{
                                        _mft(
                                            'navigation:backOffice.widget.switchCompany'
                                        )
                                    }}
                                </template>
                            </button>
                        </MenuItem>
                        <MenuItem v-slot="{ active }">
                            <button
                                type="button"
                                class="flex w-full items-center px-3 py-2 text-left text-sm transition-colors"
                                :class="
                                    active
                                        ? 'bg-gray-700 text-white'
                                        : 'text-gray-200'
                                "
                                @click="openAiUsage"
                            >
                                <i
                                    class="fa-light fa-chart-pie w-5 text-center text-gray-400"
                                    :class="isEffectivelyExpanded ? 'mr-2' : ''"
                                ></i>
                                <template v-if="isEffectivelyExpanded">
                                    {{
                                        _mft('navigation:fixedActions.aiUsage')
                                    }}
                                </template>
                            </button>
                        </MenuItem>
                        <MenuItem v-slot="{ active }">
                            <button
                                type="button"
                                class="flex w-full items-center px-3 py-2 text-left text-sm transition-colors"
                                :class="[
                                    active
                                        ? 'bg-gray-700 text-white'
                                        : 'text-gray-200',
                                    isEffectivelyExpanded ? '' : 'justify-center'
                                ]"
                                @click="openNotifications"
                                :aria-label="_mft('notification:domainNamePlural')"
                            >
                                <span class="relative">
                                    <i
                                        class="fa-bell text-center mx-auto w-5"
                                        :class="[notificationCount > 0 ? 'fa-solid animate-pop text-red-500' : 'fa-light text-gray-400', isEffectivelyExpanded ? 'mr-2' : '', notificationCount > 0 && !isEffectivelyExpanded ? '-ml-1' : '']"
                                    ></i>
                                    <span
                                        v-if="notificationCount > 0 && !isEffectivelyExpanded"
                                        class="absolute bottom-1/2 -right-1 flex items-center justify-center h-4 text-[9px] font-bold text-white"
                                    >{{ notificationCount }}</span>
                                </span>
                                <template
                                    v-if="isEffectivelyExpanded"
                                >
                                    {{ _mft('notification:domainNamePlural') }}
                                    <span
                                        v-if="notificationCount > 0"
                                        class="ml-2 flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-bold text-white bg-red-500 rounded-full"
                                    >{{ notificationCount }}</span>
                                </template>
                            </button>
                        </MenuItem>
                        <MenuItem v-slot="{ active }">
                            <button
                                type="button"
                                class="flex w-full items-center px-3 py-2 text-left text-sm transition-colors"
                                :class="
                                    active
                                        ? 'bg-gray-700 text-white'
                                        : 'text-gray-200'
                                "
                                @click="logout"
                            >
                                <i
                                    class="fa-light fa-arrow-right-from-bracket w-5 text-center text-gray-400"
                                    :class="isEffectivelyExpanded ? 'mr-2' : ''"
                                ></i>
                                <template v-if="isEffectivelyExpanded">
                                    {{
                                        _mft(
                                            'navigation:backOffice.widget.logOut'
                                        )
                                    }}
                                </template>
                            </button>
                        </MenuItem>
                    </MenuItems>
                </transition>
            </Menu>
            <!-- Support -->
            <button
                type="button"
                @click="openSupport"
                class="flex flex-col items-center justify-center rounded-xl py-1.5 hover:bg-brandPrimary-50 transition-colors translate-y-0.5"
                :aria-label="_mft('navigation:fixedActions.support')"
            >
                <i
                    class="fa-message-dots text-myflowPurple text-lg h-5"
                    :class="unreadSupportMessages > 0 ? 'fa-solid animate-pop' : 'fa-light'"
                ></i>
                <span
                    v-if="isEffectivelyExpanded"
                    class="text-xxs text-gray-400 mt-0.5"
                >
                    {{ _mft('navigation:fixedActions.support') }}
                </span>
            </button>

            <!-- Flowie -->
            <button
                type="button"
                @click="openFlowie"
                class="flex flex-col items-center justify-center rounded-xl py-1.5 transition-colors"
                :class="
                    showFlowie
                        ? ''
                        : ' hover:bg-brandPrimary-50'
                "
                :aria-label="_mft('navigation:fixedActions.flowie')"
            >
                <ai-agent-ai-orchestrator-avatar class="w-6 h-6" />
                <span v-if="isEffectivelyExpanded" class="text-xxs text-gray-400 mt-0.5">
                    {{ _mft('dashboard:aiSpecialists.flowie.name') }}
                </span>
            </button>

        </div>

        <!-- Flowie chat modal -->
        <Teleport to="body">
            <transition
                enter-active-class="transition duration-200 ease-out"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition duration-150 ease-in"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div
                    v-if="showFlowie"
                    class="fixed inset-0 bottom-4 z-modal flex items-end justify-start p-4"
                >
                    <div
                        class="fixed inset-0 bg-black/20"
                        @click="showFlowie = false"
                    ></div>
                    <div
                        class="relative z-10 w-full max-w-lg max-h-[80vh] overflow-y-auto mb-4"
                    >
                        <dashboard-flowie-chat
                            :focus-on-load="true"
                        />
                    </div>
                </div>
            </transition>
        </Teleport>

        <!-- AI Usage modal -->
        <component
            v-if="showAiUsage"
            is="utility-ai-usage"
            :start-in-purchase="aiUsageStartInPurchase"
            @close="showAiUsage = false"
        />
    </div>
</template>
